/**
 * Admin Disputes Tab
 * View and manage user-reported settlement disputes
 */

import { LoadingSpinner } from '@components/ui';
import { useAdminDisputes } from '@hooks/useAdminDisputes';
import {
  AlertTriangle,
  Ban,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  RefreshCw,
  ShieldAlert,
} from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { DisputeDetailModal } from './DisputeDetailModal';
import {
  DISPUTE_STATUS_LABELS,
  DISPUTE_STATUS_STYLES,
  formatAdminDate,
  formatCents,
} from './utils/adminFormatters';

// =============================================================================
// Component
// =============================================================================

export function DisputesTab(): ReactElement {
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const limit = 20;

  const {
    disputes,
    total,
    pages,
    loading,
    error,
    stats,
    isDetailModalOpen,
    selectedDispute,
    detailLoading,
    openDisputeDetail,
    closeDisputeDetail,
    resolveDispute,
    unbanUser,
    reactivateSubscription,
    refetch,
  } = useAdminDisputes({ status: statusFilter, page, limit });

  const statusFilters = ['all', 'open', 'won', 'lost'];

  return (
    <div className="space-y-6">
      {/* Controls Row */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        {/* Status Filters */}
        <div className="flex gap-2">
          {statusFilters.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => {
                setStatusFilter(s);
                setPage(1);
              }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                statusFilter === s
                  ? 'bg-theme-primary-fill text-theme-on-primary-fill'
                  : 'bg-theme-surface text-theme-text-secondary hover:text-theme-text'
              }`}
            >
              {s === 'all' ? 'All' : (DISPUTE_STATUS_LABELS[s] ?? s)}
            </button>
          ))}
        </div>

        {/* Refresh */}
        <button
          type="button"
          onClick={() => refetch()}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-theme-surface text-theme-text-secondary hover:text-theme-text"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Issue 1: Summary cards use aggregate stats from backend, not page data */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard
          label="Open"
          value={stats ? String(stats.openCount) : '--'}
          color="text-yellow-500"
          icon={<ShieldAlert className="w-4 h-4" />}
        />
        <SummaryCard
          label="Total Lost"
          value={stats ? formatCents(stats.totalLostCents) : '--'}
          color="text-red-500"
          icon={<DollarSign className="w-4 h-4" />}
        />
        <SummaryCard
          label="Banned Users"
          value={stats ? String(stats.bannedUsersCount) : '--'}
          color="text-red-500"
          icon={<Ban className="w-4 h-4" />}
        />
        <SummaryCard
          label="Won"
          value={stats ? String(stats.wonCount) : '--'}
          color="text-green-500"
          icon={<AlertTriangle className="w-4 h-4" />}
        />
      </div>

      {/* Error */}
      {error && (
        <div className="bg-theme-danger/10 border border-theme-danger/20 rounded-lg p-4 text-theme-danger text-sm">
          {error}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : disputes.length === 0 ? (
        <div className="text-center py-12 text-theme-text-tertiary">
          No disputes found
          {statusFilter !== 'all'
            ? ` with status "${DISPUTE_STATUS_LABELS[statusFilter] ?? statusFilter}"`
            : ''}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-theme-surface">
                <tr>
                  <th className="text-left p-3 text-theme-text-secondary font-medium">User</th>
                  <th className="text-left p-3 text-theme-text-secondary font-medium">Reason</th>
                  <th className="text-right p-3 text-theme-text-secondary font-medium">Amount</th>
                  <th className="text-center p-3 text-theme-text-secondary font-medium">Status</th>
                  <th className="text-center p-3 text-theme-text-secondary font-medium">Banned</th>
                  <th className="text-left p-3 text-theme-text-secondary font-medium">Filed</th>
                </tr>
              </thead>
              <tbody>
                {disputes.map((dispute) => (
                  <tr
                    key={dispute.id}
                    className="border-t border-theme-border hover:bg-theme-surface/50 cursor-pointer"
                    onClick={() => openDisputeDetail(dispute.id)}
                  >
                    <td className="p-3">
                      <div>
                        <p className="text-theme-text font-medium">{dispute.userDisplayName}</p>
                        <p className="text-xs text-theme-text-tertiary">{dispute.userEmail}</p>
                      </div>
                    </td>
                    <td className="p-3 text-theme-text truncate max-w-[200px]">
                      {dispute.reasonCode}
                    </td>
                    <td className="p-3 text-right font-mono text-theme-text">
                      {formatCents(dispute.amountCents)}
                    </td>
                    <td className="p-3 text-center">
                      <span
                        className={`inline-block px-2 py-1 rounded text-xs font-medium ${DISPUTE_STATUS_STYLES[dispute.status] ?? ''}`}
                      >
                        {DISPUTE_STATUS_LABELS[dispute.status] ?? dispute.status}
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      {dispute.disputeBanned ? (
                        <Ban className="w-4 h-4 text-red-500 mx-auto" />
                      ) : (
                        <span className="text-theme-text-tertiary">--</span>
                      )}
                    </td>
                    <td className="p-3 text-theme-text-secondary text-sm">
                      {formatAdminDate(dispute.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-between text-sm text-theme-text-secondary">
              <span>
                Page {page} of {pages} ({total} total)
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1.5 rounded bg-theme-surface disabled:opacity-50 hover:bg-theme-border flex items-center gap-1"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(pages, p + 1))}
                  disabled={page === pages}
                  className="px-3 py-1.5 rounded bg-theme-surface disabled:opacity-50 hover:bg-theme-border flex items-center gap-1"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Detail Modal (Issue 3: renders when modal is open, not when data is loaded) */}
      {isDetailModalOpen && (
        <DisputeDetailModal
          dispute={selectedDispute}
          isLoading={detailLoading}
          onClose={closeDisputeDetail}
          onResolve={resolveDispute}
          onUnban={unbanUser}
          onReactivate={reactivateSubscription}
        />
      )}
    </div>
  );
}

// =============================================================================
// Sub-Components
// =============================================================================

function SummaryCard({
  label,
  value,
  color,
  icon,
}: {
  label: string;
  value: string;
  color?: string;
  icon?: ReactElement;
}): ReactElement {
  return (
    <div className="bg-theme-surface rounded-lg p-4 border border-theme-border">
      <p className="text-xs text-theme-text-tertiary">{label}</p>
      <p className={`text-xl font-bold ${color ?? 'text-theme-text'} flex items-center gap-1`}>
        {icon}
        {value}
      </p>
    </div>
  );
}
